import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { toast } from "sonner";
import { fetchDesks } from "../../services/crmServices/deskService";
import { transferAgent } from "../../services/crmServices/userService";

export function TransferAgentModal({ open, onClose, onSuccess, agent }) {
    const [targetDesk, setTargetDesk] = useState("");
    const [desks, setDesks] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchDesks().then((res) => {
                if (res?.data?.length) {
                    // Filter out the agent's current desk
                    const availableDesks = res.data.filter((desk) => desk._id !== agent?.agentDesk?._id);
                    setDesks(availableDesks);
                }
            });
        }
    }, [open, agent]);

    const handleTransfer = async () => {
        if (!targetDesk) {
            toast.error("Please select a target desk");
            return;
        }

        setLoading(true);

        try {
            const transferData = {
                agentId: agent._id,
                agentDesk: targetDesk
            };

            transferAgent(transferData).then((response) => {
                if (response.status === 200) {
                    toast.success(`Successfully transferred ${agent.firstName} ${agent.lastName}`);
                    onSuccess();
                } else if (response?.data.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.error("Failed to transfer agent. Try again later.");
                }
            });
        } catch (error) {
            console.error("Transfer error:", error);
            toast.error("Failed to transfer agent");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setTargetDesk("");
        onClose();
    };

    if (!agent) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Transfer Agent</DialogTitle>
                    <DialogDescription>
                        Transfer {agent.firstName} {agent.lastName} to a different desk.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Agent Details</h4>
                        <p className="text-sm text-gray-600">
                            Name: {agent.firstName} {agent.lastName}
                        </p>
                        <p className="text-sm text-gray-600">
                            Current Desk: {agent.agentDesk?.name || "No desk assigned"}
                        </p>
                        <p className="text-sm text-gray-600">Position: {agent.position.name}</p>
                    </div>

                    <div>
                        <Label htmlFor="targetDesk">Target Desk</Label>
                        <Select
                            options={desks.map((desk) => ({
                                label: desk.name,
                                value: desk._id
                            }))}
                            value={targetDesk}
                            onChange={setTargetDesk}
                            placeholder="Select target desk"
                            searchable
                        />
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-2">Transfer Impact</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Agent will be moved to the selected desk</li>
                            <li>• Current lead assignments will be preserved</li>
                            <li>• Agent permissions will remain unchanged</li>
                            <li>• Transfer will be logged in agent activity</li>
                        </ul>
                    </div>

                    <div className="flex space-x-3">
                        <Button
                            onClick={handleTransfer}
                            disabled={loading || !targetDesk}
                            className="flex-1 "
                        >
                            {loading ? "Transferring..." : "Transfer Agent"}
                        </Button>
                        <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
